import { Injectable } from '@angular/core';

export interface StripePaymentMethodResult {
	paymentMethodId: string;
	error?: string;
}

/** Defaults tuned to match Angular Material outline fields (Roboto, 16px, neutral grays). */
const DEFAULT_ELEMENTS_APPEARANCE = {
	theme: 'stripe' as const,
	variables: {
		fontFamily: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
		fontSizeBase: '16px',
		borderRadius: '4px',
		colorBackground: '#ffffff',
		colorText: 'rgba(0, 0, 0, 0.87)',
		colorTextSecondary: 'rgba(0, 0, 0, 0.6)',
		colorTextPlaceholder: 'rgba(0, 0, 0, 0.42)',
		colorDanger: '#f44336',
		colorPrimary: '#3f51b5',
		spacingUnit: '8px',
	},
};

const DEFAULT_CARD_STYLE = {
	base: {
		fontSize: '16px',
		color: 'rgba(0, 0, 0, 0.87)',
		fontFamily: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
		fontSmoothing: 'antialiased',
		'::placeholder': { color: 'rgba(0, 0, 0, 0.42)' },
		iconColor: 'rgba(0, 0, 0, 0.54)',
	},
	invalid: {
		color: '#f44336',
		iconColor: '#f44336',
	},
};

@Injectable({
	providedIn: 'root'
})
export class StripeService {
	private stripe: any = null;
	private elements: any = null;
	private cardElement: any = null;
	private currentKey: string = '';

	async init(publishableKey: string): Promise<boolean> {
		if (!publishableKey || (this.stripe && this.currentKey === publishableKey)) {
			return !!this.stripe;
		}
		this.currentKey = publishableKey;
		try {
			const loadStripe = (await import('@stripe/stripe-js')).loadStripe;
			this.stripe = await loadStripe(publishableKey);
			this.elements = null;
			this.cardElement = null;
			return !!this.stripe;
		} catch {
			return false;
		}
	}

	isReady(): boolean {
		return !!this.stripe;
	}

	canCreatePaymentMethod(): boolean {
		return !!(this.stripe && this.cardElement);
	}

	mountCardElement(container: HTMLElement, options?: { style?: object; appearance?: object }): Promise<boolean> {
		if (!this.stripe || !container) {
			return Promise.resolve(false);
		}
		if (this.cardElement) {
			this.cardElement.unmount();
			this.cardElement = null;
		}
		if (!this.elements) {
			this.elements = this.stripe.elements({
				appearance: (options?.appearance ?? DEFAULT_ELEMENTS_APPEARANCE) as any,
			});
		}
		this.cardElement = this.elements.create('card', {
			style: (options?.style ?? DEFAULT_CARD_STYLE) as any,
		});
		this.cardElement.mount(container);
		return Promise.resolve(true);
	}

	unmountCardElement(): void {
		if (this.cardElement) {
			this.cardElement.unmount();
			this.cardElement = null;
		}
	}

	async createPaymentMethod(billingName?: string): Promise<StripePaymentMethodResult> {
		if (!this.stripe || !this.cardElement) {
			return {
				paymentMethodId: '',
				error: 'Secure card fields did not load. Please refresh the page and try again, or contact support if it persists.'
			};
		}
		try {
			const payload: any = { type: 'card', card: this.cardElement };
			if (billingName) {
				payload.billing_details = { name: billingName };
			}
			const { error, paymentMethod } = await this.stripe.createPaymentMethod(payload);
			if (error) {
				return { paymentMethodId: '', error: error.message };
			}
			return { paymentMethodId: paymentMethod.id };
		} catch (e: any) {
			return { paymentMethodId: '', error: e?.message || 'Failed to create payment method' };
		}
	}
}
